import { useCallback, useEffect, useState } from "react";
import {
  buildCharacterizationReportQueryTargets,
  mapWithConcurrency,
} from "../../dashboard/dashboardKpiUtils";
import {
  getCharacterizationContainersList,
  getListRotationPeriods,
  getSuperContainerList,
} from "../../../services/container";
import { collectAllReportPeriods } from "../core/utils/characterizationViewMode";
import { buildSites } from "./useCharacterizationSites";

const SITE_PERIOD_FETCH_CONCURRENCY = 4;

export const useAllRotationPeriods = ({
  enabled = false,
  periodMaterialStart,
  periodMaterialStop,
}) => {
  const [periods, setPeriods] = useState([]);
  const [sites, setSites] = useState([]);
  const [superContainers, setSuperContainers] = useState([]);
  const [containers, setContainers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAllPeriods = useCallback(async () => {
    if (!enabled) {
      setPeriods([]);
      return [];
    }
    setIsLoading(true);
    setError(null);
    try {
      const [superResponse, containersResponse] = await Promise.all([
        getSuperContainerList(),
        getCharacterizationContainersList({ characterizationOnly: false }),
      ]);
      if (containersResponse.status !== 200) {
        throw new Error("Unable to load containers");
      }
      const nextContainers = containersResponse?.data?.data ?? [];
      const nextSupers = superResponse?.data?.superContainers ?? [];
      const nextSites = buildSites(nextContainers, nextSupers);
      setContainers(nextContainers);
      setSuperContainers(nextSupers);
      setSites(nextSites);

      const targets = buildCharacterizationReportQueryTargets(
        nextContainers,
        nextSupers,
      );
      const groups = await mapWithConcurrency(
        targets,
        SITE_PERIOD_FETCH_CONCURRENCY,
        async (target) => {
          try {
            const params = {
              periodMaterialStart,
              periodMaterialStop,
              isCharacterization: true,
            };
            if (target.superContainerId) {
              params.superContainerId = target.superContainerId;
            }
            const response = await getListRotationPeriods(
              params,
              target.containerId,
            );
            return {
              target,
              periods: response?.data?.periods ?? [],
            };
          } catch (zoneError) {
            console.error(
              "Failed to fetch characterization periods for zone",
              target?.containerId,
              zoneError,
            );
            return { target, periods: [] };
          }
        },
      );
      const flattened = collectAllReportPeriods(groups, nextSites);
      setPeriods(flattened);
      return flattened;
    } catch (err) {
      console.error("Failed to fetch all characterization periods", err);
      setError(err);
      setPeriods([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [enabled, periodMaterialStart, periodMaterialStop]);

  useEffect(() => {
    fetchAllPeriods();
  }, [fetchAllPeriods]);

  return {
    periods,
    sites,
    superContainers,
    containers,
    isLoading,
    error,
    refetch: fetchAllPeriods,
  };
};
